/**
 * The personal layer over the design tokens (ADR 0009 §8).
 *
 * A brand override and a person's own appearance both re-point `--ds-*`
 * properties on `<html>`, and they must not overwrite each other's work: the
 * brand decides the identity, the person decides everything they were allowed
 * to. Each layer therefore remembers what it set and removes only that.
 *
 * Applied through the CSSOM, like the brand, so the strict Content-Security-
 * Policy needs no `style-src 'unsafe-inline'`.
 */

/** Custom properties to set on `<html>`, keyed by property name (`--ds-brand`, …). */
export type TokenDeclarations = Readonly<Record<string, string>>;

const CUSTOM_PROPERTY_PREFIX = '--';

/** What each layer set last time, so the next call removes exactly what it replaced. */
const applied = new Map<string, readonly string[]>();

/**
 * Replaces one layer's token overrides on the document.
 *
 * @param layer independent name, e.g. `brand` or `appearance`
 * @throws TypeError when a key is not a CSS custom property (`--…`)
 */
export function applyTokenLayer(layer: string, declarations: TokenDeclarations): void {
  const names = Object.keys(declarations);
  const invalid = names.filter((name) => !name.startsWith(CUSTOM_PROPERTY_PREFIX));
  if (invalid.length > 0) {
    throw new TypeError(`applyTokenLayer expects CSS custom properties, received: ${invalid.join(', ')}`);
  }

  const { style } = document.documentElement;
  for (const name of applied.get(layer) ?? []) {
    if (!(name in declarations)) style.removeProperty(name);
  }
  for (const [name, value] of Object.entries(declarations)) style.setProperty(name, value);
  applied.set(layer, names);
}

/** Removes one layer, leaving the others as they are. */
export function clearTokenLayer(layer: string): void {
  const { style } = document.documentElement;
  for (const name of applied.get(layer) ?? []) style.removeProperty(name);
  applied.delete(layer);
}

/** The layer a person's own settings write into. */
export const APPEARANCE_LAYER = 'appearance';

/** Applies what the person chose, over whatever the brand set. */
export function applyAppearance(declarations: TokenDeclarations): void {
  applyTokenLayer(APPEARANCE_LAYER, declarations);
}
